import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import NotFound from './NotFound';
import { projects, type Project } from '../data/projects';

const statusStyles: Record<Project['status'], string> = {
  Live: 'bg-emerald-500/10 text-emerald-300 border-emerald-400/30',
  Concept: 'bg-amber-500/10 text-amber-300 border-amber-400/30',
  'Case study': 'bg-accent-500/10 text-accent-400 border-accent-500/30',
};

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const project = projects.find((p) => p.slug === slug);

  if (!project) {
    return <NotFound />;
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.title,
    description: project.summary,
    url: `https://nexsollabs.com/projects/${project.slug}`,
    keywords: project.tags.join(', '),
    creator: { '@id': 'https://nexsollabs.com/#organization' },
    about: project.category,
    dateCreated: String(project.year),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://nexsollabs.com/' },
      { '@type': 'ListItem', position: 2, name: 'Projects', item: 'https://nexsollabs.com/projects' },
      { '@type': 'ListItem', position: 3, name: project.title, item: `https://nexsollabs.com/projects/${project.slug}` },
    ],
  };

  const otherProjects = projects.filter((p) => p.slug !== project.slug).slice(0, 3);

  return (
    <>
      <SEO
        title={`${project.title} — Case Study | Nexsol Labs`}
        description={project.summary}
        path={`/projects/${project.slug}`}
        keywords={[project.title, project.category, ...project.tags, 'Nexsol Labs case study', 'custom software Skopje'].join(', ')}
        jsonLd={[jsonLd, breadcrumbJsonLd]}
      />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-page relative py-16 md:py-24">
          <Link
            to="/projects"
            className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-4 w-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 12H5M11 6l-6 6 6 6" />
            </svg>
            All projects
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 max-w-3xl"
          >
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-accent-400">
                {project.category}
              </p>
              <span
                className={`inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${
                  statusStyles[project.status]
                }`}
              >
                {project.status}
              </span>
              <span className="text-xs text-white/40">{project.year}</span>
            </div>
            <h1 className="mt-5 font-display text-4xl font-semibold leading-tight tracking-tight text-white md:text-5xl">
              {project.title}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-white/75 md:text-lg">
              {project.summary}
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {project.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 font-mono text-[11px] text-white/70"
                >
                  {tag}
                </span>
              ))}
            </div>

            {project.link && (
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  href={project.link}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                >
                  Visit live site
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    className="h-4 w-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M14 5h5v5M19 5l-9 9M5 5h5M5 5v14h14v-5"
                    />
                  </svg>
                </a>
                <Link to="/contact" className="btn-ghost">
                  Discuss a similar project
                </Link>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* PROBLEM + APPROACH */}
      <section className="container-page pb-16 md:pb-24">
        <div className="grid gap-8 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5 }}
            className="card"
          >
            <p className="eyebrow">The problem</p>
            <p className="mt-5 text-base leading-relaxed text-white/75">
              {project.problem}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="card"
          >
            <p className="eyebrow">Our approach</p>
            <p className="mt-5 text-base leading-relaxed text-white/75">
              {project.approach}
            </p>
          </motion.div>
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className="container-page pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="eyebrow">What shipped</p>
          <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight text-white md:text-4xl">
            The pieces that make this work in daily use.
          </h2>
        </motion.div>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {project.highlights.map((h, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-5"
            >
              <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500/15 text-accent-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  className="h-3 w-3"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5L20 7" />
                </svg>
              </span>
              <p className="text-sm leading-relaxed text-white/75">{h}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* METRICS */}
      {project.metrics && project.metrics.length > 0 && (
        <section className="container-page pb-16 md:pb-24">
          <div className="grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5 md:grid-cols-3">
            {project.metrics.map((m) => (
              <div
                key={m.label}
                className="bg-ink-950/80 px-6 py-7 text-center"
              >
                <p className="font-display text-xl font-semibold text-white md:text-2xl">
                  {m.value}
                </p>
                <p className="mt-1.5 text-xs uppercase tracking-[0.16em] text-white/55">
                  {m.label}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* OUTCOME */}
      <section className="container-page pb-16 md:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="rounded-3xl border border-white/10 bg-gradient-to-br from-accent-500/15 via-teal-500/10 to-transparent p-8 md:p-12"
        >
          <p className="eyebrow">Outcome</p>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-white/80 md:text-xl">
            {project.outcome}
          </p>
        </motion.div>
      </section>

      {/* NEXT PROJECTS */}
      <section className="container-page pb-20 md:pb-28">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <h2 className="font-display text-2xl font-semibold text-white md:text-3xl">
            Other things we've built
          </h2>
          <Link
            to="/projects"
            className="text-sm font-medium text-accent-400 hover:text-accent-500"
          >
            See all projects →
          </Link>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {otherProjects.map((p) => (
            <Link
              key={p.slug}
              to={`/projects/${p.slug}`}
              className="card group block"
            >
              <p className="text-xs font-medium uppercase tracking-[0.18em] text-accent-400">
                {p.category}
              </p>
              <h3 className="mt-2 font-display text-lg font-semibold text-white">
                {p.title}
              </h3>
              <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-white/65">
                {p.summary}
              </p>
              <p className="mt-4 text-xs font-medium text-accent-400 opacity-0 transition group-hover:opacity-100">
                Read case study →
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-page pb-20 md:pb-28">
        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-10 text-center">
          <h2 className="font-display text-2xl font-semibold text-white md:text-3xl">
            Have something similar in mind?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-white/65">
            We'd be happy to look at it with you. No funnel, no boilerplate — an honest
            read on whether it's worth building and what it would take.
          </p>
          <div className="mt-6 flex justify-center">
            <Link to="/contact" className="btn-primary">
              Start a conversation
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
